import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaInfoCircle, FaStar, FaStarHalfAlt, FaRegStar, FaChevronDown, FaChevronUp } from "react-icons/fa";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import "./SatisfactionShared.css";

// Phase 26. Reuses the exact same .td-card/.td-ch/.td-loader/.td-empty/
// .td-btn/.td-sel classes TechnicianPerformancePanel.jsx already defines
// globally (PortalHomeCards.css) -- only new, panel-scoped classes (tsp-*)
// are defined in the <style> block below, mirroring that file's own
// tpp-* convention.

function currentMonthValue() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function InfoTooltip({ text }) {
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
      <span className="tsp-info" role="img" aria-label="How this is calculated">
        <FaInfoCircle />
      </span>
    </OverlayTrigger>
  );
}

function KpiCard({ title, value, subtext, tooltip, accent }) {
  return (
    <div className={`td-card tsp-kpi-card${accent ? ` tsp-kpi-card--${accent}` : ""}`}>
      <div className="tsp-kpi-head">
        <span className="tsp-kpi-title">{title}</span>
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      <div className="tsp-kpi-value">{value}</div>
      {subtext && <div className="tsp-kpi-sub">{subtext}</div>}
    </div>
  );
}

// Renders `rating` (0-5, half-point precision) as 5 stars: full/half/empty.
function StarRow({ rating }) {
  const stars = [];
  for (let i = 1; i <= 5; i += 1) {
    const diff = rating - (i - 1);
    if (diff >= 1) stars.push(<FaStar key={i} />);
    else if (diff >= 0.5) stars.push(<FaStarHalfAlt key={i} />);
    else stars.push(<FaRegStar key={i} />);
  }
  return <div className="tsp-star-row" aria-hidden="true">{stars}</div>;
}

function RatingSpotlight({ averageRating, ratingPoints, maxPoints, tooltip }) {
  const hasAverage = averageRating != null;
  const hasPoints = ratingPoints != null && maxPoints != null && maxPoints > 0;
  const averagePercent = hasAverage ? Math.round((averageRating / 5) * 100) : null;
  const pointsPercent = hasPoints ? Math.round((ratingPoints / maxPoints) * 100) : null;

  return (
    <div className="tsp-spotlight">
      <div className="tsp-spotlight-card">
        <div className="tsp-kpi-head">
          <span className="tsp-kpi-title">Average Rating</span>
          <InfoTooltip text="Cumulative raw rating points ÷ cumulative rating answers -- never an average of monthly averages." />
        </div>
        {hasAverage ? (
          <>
            <StarRow rating={averageRating} />
            <div className="tsp-spotlight-value">
              {averageRating}<span className="tsp-spotlight-max">/5</span>
              <span className="tsp-points-percent">{averagePercent}%</span>
            </div>
          </>
        ) : (
          <div className="tsp-spotlight-value tsp-spotlight-value--muted">—</div>
        )}
      </div>

      <div className="tsp-spotlight-card">
        <div className="tsp-kpi-head">
          <span className="tsp-kpi-title">Rating Points</span>
          <InfoTooltip text={tooltip} />
        </div>
        {hasPoints ? (
          <>
            <div className="tsp-points-bar">
              <div className="tsp-points-bar-fill" style={{ width: `${pointsPercent}%` }} />
            </div>
            <div className="tsp-spotlight-value">
              {ratingPoints}<span className="tsp-spotlight-max">/{maxPoints}</span>
              <span className="tsp-points-percent">{pointsPercent}%</span>
            </div>
          </>
        ) : (
          <div className="tsp-spotlight-value tsp-spotlight-value--muted">—</div>
        )}
      </div>
    </div>
  );
}

// Exported: reused as-is by pages/forms/TicketFeedbackFormsTab.jsx (the
// "Ticket Feedback Forms" tab on the Technician's Assigned Forms page) so
// the exact same status vocabulary/copy never drifts between the two
// surfaces showing the SAME task_satisfaction_requests population.
export function statusLabel(status) {
  switch (status) {
    case "SUBMITTED": return "Submitted";
    case "SENT": return "Awaiting Response";
    case "LINK_PENDING": return "Link Pending";
    case "FAILED": return "Failed";
    case "REVOKED": return "Revoked";
    default: return status;
  }
}

// Exported for the same reason as statusLabel above -- one rating-breakdown
// renderer for GET /technician/satisfaction-summary/feedback/:submissionId,
// reused by TicketFeedbackFormsTab.jsx rather than a second implementation.
// `compact` (Ticket Feedback Forms tab): for a one-question response the
// Total/Average rows only repeat the single rating, so they are left out.
export function FeedbackDetail({ submissionId, compact = false }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/technician/satisfaction-summary/feedback/${submissionId}`)
      .then((res) => { if (!cancelled) setDetail(res.data?.data || null); })
      .catch(() => { if (!cancelled) setDetail(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [submissionId]);

  if (loading) return <div className="tsp-feedback-loading"><Spinner animation="border" size="sm" /></div>;
  if (!detail) return <div className="tsp-feedback-empty">Feedback could not be loaded.</div>;

  const showTotals = !(compact && detail.ratings.length === 1);

  return (
    <div className={`tsp-feedback-detail${compact ? " tsp-feedback-detail--compact" : ""}`}>
      {detail.ratings.map((r) => (
        <div key={r.field_id} className="tsp-feedback-row">
          <span>{r.label}</span>
          <strong>{r.value != null ? `${r.value} / ${r.max ?? "—"}` : "—"}</strong>
        </div>
      ))}
      {showTotals && (
        <>
          <div className="tsp-feedback-row tsp-feedback-total">
            <span>Total</span>
            {/* null when no question was answered -- shown as "—", never 0. */}
            <strong>{detail.total_points != null ? `${detail.total_points} / ${detail.total_max_points ?? "—"}` : "—"}</strong>
          </div>
          <div className="tsp-feedback-row">
            <span>Average</span>
            <strong>{detail.average_rating != null ? `${detail.average_rating} / 5` : "—"}</strong>
          </div>
        </>
      )}
      {detail.comments && <div className="tsp-feedback-comments">“{detail.comments}”</div>}
    </div>
  );
}

const TechnicianSatisfactionPanel = () => {
  const { user } = useContext(AuthContext) || {};

  const [month, setMonth] = useState(currentMonthValue());
  const [viewMode, setViewMode] = useState("monthly"); // "monthly" | "cumulative"

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showDrilldown, setShowDrilldown] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [expandedRequestId, setExpandedRequestId] = useState(null);

  const fetchSummary = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/technician/satisfaction-summary", { params: { month } });
      setData(res.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load satisfaction metrics.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, month]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const fetchDrilldown = useCallback(async () => {
    if (!user?.id) return;
    setRowsLoading(true);
    try {
      const res = await api.get("/technician/satisfaction-summary/tasks", { params: { month, scope: viewMode === "cumulative" ? "cumulative" : "month" } });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      setRows([]);
    } finally {
      setRowsLoading(false);
    }
  }, [user?.id, month, viewMode]);

  useEffect(() => { if (showDrilldown) fetchDrilldown(); }, [showDrilldown, fetchDrilldown]);

  const scope = data ? (viewMode === "cumulative" ? data.cumulative : data.monthly) : null;
  const periodLabel = data
    ? (viewMode === "cumulative" ? `${data.period.start} – ${data.period.selected}` : data.period.selected)
    : "";

  return (
    <div className="td-card tsp-panel">
      <div className="td-ch">
        <strong>User Satisfaction</strong>
        <div className="tsp-view-switch" role="group" aria-label="Satisfaction View">
          <button type="button" className={`tsp-view-btn${viewMode === "monthly" ? " tsp-view-btn--active" : ""}`} onClick={() => setViewMode("monthly")}>This Month</button>
          <button type="button" className={`tsp-view-btn${viewMode === "cumulative" ? " tsp-view-btn--active" : ""}`} onClick={() => setViewMode("cumulative")}>Cumulative</button>
        </div>
      </div>

      <div className="tsp-toolbar">
        <input
          type="month"
          className="td-sel tsp-month-input"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          aria-label="Month"
        />
        <Button variant={null} className="td-btn td-btn-ghost" onClick={fetchSummary} disabled={loading}>
          <i className="fa-solid fa-rotate" aria-hidden="true"></i>{loading ? "..." : "Refresh"}
        </Button>
      </div>

      {loading && !data && (
        <div className="td-loader"><Spinner animation="border" size="sm" /><p>Loading satisfaction metrics…</p></div>
      )}

      {!loading && error && (
        <div className="td-empty">
          <div className="td-ei"><i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i></div>
          <p>{error}</p>
          <Button variant={null} className="td-btn td-btn-primary td-btn-xs" style={{ marginTop: 6 }} onClick={fetchSummary}>
            <i className="fa-solid fa-rotate-right" aria-hidden="true"></i>Try Again
          </Button>
        </div>
      )}

      {!error && scope && (
        <>
          <div className="tsp-period-caption">{periodLabel}</div>

          {scope.eligible_completed_tasks === 0 ? (
            <div className="td-empty">
              <div className="td-ei"><i className="fa-solid fa-inbox" aria-hidden="true"></i></div>
              <p>No eligible completed tasks in this period.</p>
            </div>
          ) : (
            <>
              <RatingSpotlight
                averageRating={scope.average_rating}
                ratingPoints={scope.rating_answers ? scope.rating_points_received : null}
                maxPoints={scope.rating_answers ? scope.maximum_possible_points : null}
                tooltip="Sum of every rating value received ÷ the maximum possible for that many answers, derived from the actual configured field scale."
              />
              <div className="tsp-kpi-grid">
                <KpiCard title="Completed Eligible Tasks" value={scope.eligible_completed_tasks} accent="blue" />
                <KpiCard title="Feedback Requests Sent" value={scope.requests_sent} accent="blue" />
                <KpiCard title="Responses Received" value={scope.responses_received} accent="green" />
                <KpiCard
                  title="Pending Feedback"
                  value={scope.pending}
                  subtext={scope.unresolved > 0 ? `${scope.unresolved} need attention (link/email failed)` : undefined}
                  accent={scope.unresolved > 0 ? "orange" : undefined}
                />
                <KpiCard
                  title="Response Rate"
                  value={scope.response_rate_percent != null ? `${scope.response_rate_percent}%` : "—"}
                  tooltip="Responses received ÷ feedback requests sent × 100. Counts unique requests, never one per rating question."
                  accent="green"
                />
                {scope.kpi_rating_value != null && (
                  <>
                    <KpiCard title="KPI Rating" value={`${scope.kpi_rating_value} / 5`} accent="green" />
                    <KpiCard title="Normalized KPI Score" value={`${scope.kpi_normalized_score_percent}%`} accent="green" />
                  </>
                )}
              </div>

              <div className="tsp-drilldown-toggle">
                <Button variant={null} className="td-btn td-btn-ghost td-btn-xs" onClick={() => setShowDrilldown((v) => !v)}>
                  {showDrilldown ? "Hide Feedback Tasks" : "View Feedback Tasks"}
                </Button>
              </div>

              {showDrilldown && (
                <div className="tsp-drilldown">
                  <div className="tsp-drilldown-heading">Feedback Tasks</div>
                  {rowsLoading && <div className="td-loader"><Spinner animation="border" size="sm" /></div>}
                  {!rowsLoading && !rows.length && <div className="td-empty"><p>No requests in this period.</p></div>}
                  {!rowsLoading && rows.length > 0 && (
                    <div className="tsp-drilldown-table-wrap">
                      <table className="tsp-drilldown-table tsp-drilldown-table--compact">
                        <thead>
                          <tr>
                            <th>Task</th>
                            <th>Requester</th>
                            <th>Completed</th>
                            <th>Status</th>
                            <th aria-label="Actions"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => {
                            const expanded = expandedRequestId === row.request_id;
                            return (
                              <React.Fragment key={row.request_id}>
                                <tr className={expanded ? "tsp-row--expanded" : undefined}>
                                  <td className="tsp-task-cell">
                                    <span className="tsp-task-id">#{row.task_id}</span>
                                    <span className="tsp-task-title" title={row.task_title}>{row.task_title}</span>
                                  </td>
                                  <td>{row.requester_name}</td>
                                  <td className="tsp-nowrap">{row.completed_at ? new Date(row.completed_at).toLocaleDateString() : "—"}</td>
                                  <td className="tsp-nowrap">
                                    <span className={`tsp-status-pill tsp-status-pill--${row.status.toLowerCase()}`}>{statusLabel(row.status)}</span>
                                  </td>
                                  <td className="tsp-action-cell">
                                    {row.status === "SUBMITTED" && row.submission_id && (
                                      <Button
                                        variant={null}
                                        className="td-btn td-btn-ghost td-btn-xs tsp-view-feedback-btn"
                                        aria-expanded={expanded}
                                        onClick={() => setExpandedRequestId(expanded ? null : row.request_id)}
                                      >
                                        {expanded ? "Hide" : "View"}
                                        {expanded ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                                {expanded && row.submission_id && (
                                  <tr className="tsp-detail-row">
                                    <td colSpan={5}><FeedbackDetail submissionId={row.submission_id} /></td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      <style>{`
        .tsp-panel{margin-top:0;margin-bottom:16px;}
        .tsp-toolbar{display:flex;gap:8px;align-items:center;padding:10px 16px 14px;border-bottom:1px solid #f1f5f9;}
        .tsp-month-input{width:auto;flex:0 0 auto;min-width:150px;}
        .tsp-view-switch{display:inline-flex;flex-shrink:0;border:1px solid #e3c7d0;border-radius:8px;overflow:hidden;height:40px;}
        .tsp-view-btn{border:none;background:#fff;color:#7a1236;font-size:11px;font-weight:700;padding:0 14px;cursor:pointer;height:100%;}
        .tsp-view-btn--active{background:#7a1236;color:#fff;}
        .tsp-view-btn:focus-visible{outline:2px solid #7a1236;outline-offset:-2px;}
        .tsp-spotlight{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;padding:16px 16px 0;}
        .tsp-spotlight-card{background:linear-gradient(135deg,#f5f3ff,#faf5ff);border:1px solid #e9d5ff;border-radius:14px;padding:16px 18px;}
        .tsp-star-row{display:flex;gap:4px;margin-top:10px;color:#a855f7;font-size:20px;}
        .tsp-spotlight-value{font-size:32px;font-weight:800;color:#6d28d9;margin-top:8px;line-height:1;display:flex;align-items:baseline;gap:6px;}
        .tsp-spotlight-value--muted{color:#94a3b8;font-weight:700;font-size:20px;}
        .tsp-spotlight-max{font-size:14px;font-weight:700;color:#a78bfa;}
        .tsp-points-percent{margin-left:auto;font-size:12px;font-weight:700;color:#7c3aed;}
        .tsp-points-bar{margin-top:10px;height:8px;border-radius:999px;background:#e9d5ff;overflow:hidden;}
        .tsp-points-bar-fill{height:100%;border-radius:999px;background:linear-gradient(90deg,#a855f7,#7c3aed);transition:width .3s ease;}
        @media(max-width:640px){.tsp-spotlight{grid-template-columns:1fr;}}
        .tsp-kpi-grid{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px;padding:16px;}
        .tsp-kpi-card{padding:16px;box-shadow:none;border-radius:14px;min-height:110px;}
        .tsp-kpi-card--blue{border-color:#bfdbfe;background:#eff6ff;}
        .tsp-kpi-card--green{border-color:#bbf7d0;background:#ecfdf5;}
        .tsp-kpi-card--purple{border-color:#e9d5ff;background:#f5f3ff;}
        .tsp-kpi-card--orange{border-color:#fed7aa;background:#fff7ed;}
        .tsp-kpi-head{display:flex;align-items:center;justify-content:space-between;gap:6px;}
        .tsp-kpi-title{font-size:12px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.05em;}
        .tsp-info{color:#94a3b8;cursor:help;display:inline-flex;flex-shrink:0;}
        .tsp-kpi-value{font-size:26px;font-weight:700;color:#14151f;margin-top:6px;line-height:1.15;}
        .tsp-kpi-card--blue .tsp-kpi-value{color:#2563eb;}
        .tsp-kpi-card--green .tsp-kpi-value{color:#16a34a;}
        .tsp-kpi-card--purple .tsp-kpi-value{color:#7c3aed;}
        .tsp-kpi-card--orange .tsp-kpi-value{color:#c2410c;}
        .tsp-kpi-sub{font-size:11px;color:#64748b;font-weight:600;margin-top:4px;}
        .tsp-drilldown-toggle{padding:0 16px 10px;}
        .tsp-drilldown{border-top:1px solid #f1f5f9;padding:10px 16px 16px;}
        .tsp-drilldown-table--compact{min-width:0;}
        .tsp-drilldown-table--compact th,.tsp-drilldown-table--compact td{padding-left:10px;padding-right:10px;}
        .tsp-drilldown-table--compact th:last-child,.tsp-drilldown-table--compact td.tsp-action-cell{width:1%;text-align:right;white-space:nowrap;}
        .tsp-task-cell{min-width:140px;}
        .tsp-task-id{display:inline-block;font-size:11px;font-weight:800;color:var(--primary,#6d1530);background:#f8eef1;border-radius:6px;padding:1px 6px;margin-bottom:3px;}
        .tsp-task-title{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;line-height:1.35;word-break:break-word;}
        .tsp-nowrap{white-space:nowrap;}
        .tsp-view-feedback-btn{display:inline-flex;align-items:center;gap:6px;white-space:nowrap;}
        .tsp-view-feedback-btn svg{font-size:10px;}
        .tsp-drilldown-table tbody tr.tsp-row--expanded td{background:var(--brand-gold-soft,#f6ecd7);border-bottom:none;}
        .tsp-drilldown-table tbody tr.tsp-detail-row td{background:var(--brand-gold-soft,#f6ecd7);padding:0 10px 12px;}
        .tsp-detail-row .tsp-feedback-detail{background:#fff;border:1px solid #eadfcb;border-left:3px solid var(--primary,#6d1530);}
        .tsp-drilldown-heading{font-size:11px;font-weight:800;color:#475569;text-transform:uppercase;letter-spacing:.05em;margin-bottom:8px;}
        @media(max-width:900px){.tsp-kpi-grid{grid-template-columns:repeat(2,minmax(0,1fr));}}
        @media(max-width:640px){.tsp-kpi-grid{grid-template-columns:1fr;}}
      `}</style>
    </div>
  );
};

export default TechnicianSatisfactionPanel;
